import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { comics, categories, categoryLabels } from '@/mocks/comics';

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const featuredComics = comics.filter((c) => c.featured).slice(0, 5);
  const trendingComics = comics.filter((c) => c.trending).slice(0, 8);
  const latestComics = [...comics].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  ).slice(0, 8);

  const faqs = [
    {
      q: '韩漫屋的漫画都是免费的吗？',
      a: '是的，韩漫屋上的所有韩国漫画均完全免费在线观看。我们致力于为广大韩漫爱好者提供零门槛的阅读体验，您无需支付任何费用即可畅享全部内容。',
    },
    {
      q: '什么是下拉式阅读模式？',
      a: '下拉式阅读是韩国漫画（Webtoon）的特色阅读方式。您只需向下滚动页面，漫画内容就会自然呈现，无需翻页，带来如丝般顺滑的连续阅读体验，特别适合在手机和电脑上阅读。',
    },
    {
      q: '韩漫屋更新频率如何？',
      a: '韩漫屋每天都会更新最新漫画章节，通常在早上7点到晚上11点之间持续更新。热门连载漫画的更新速度尤其快，确保读者能够第一时间看到最新内容。',
    },
    {
      q: '如何在手机上阅读韩漫？',
      a: '韩漫屋的网站采用响应式设计，完美适配手机、平板和电脑。您只需在手机浏览器中打开我们的网站，即可享受与App同等的阅读体验，无需下载任何应用。',
    },
    {
      q: '韩漫屋支持哪些类型的韩国漫画？',
      a: '我们涵盖了恋爱、奇幻、动作、悬疑、搞笑、剧情、耽美、百合等八大热门分类，无论您喜欢什么类型的韩漫，都能在这里找到心仪的作品。',
    },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫屋',
    url: 'https://hanmanwu.com',
    description: '韩漫屋拥有完整的韩国漫画大全，免费在线观看并采用下拉式界面，呈现好看的韩漫供您探索。',
    sameAs: ['https://weibo.com/hanmanwu'],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫屋',
    url: 'https://hanmanwu.com',
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://hanmanwu.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead />
      <SchemaOrg data={organizationSchema} />
      <SchemaOrg data={websiteSchema} />
      <SchemaOrg data={faqSchema} />

      <Navbar />

      <main>
        <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20Korean%20manhwa%20style%20illustration%20with%20warm%20coral%20and%20amber%20tones%2C%20abstract%20flowing%20lines%20reminiscent%20of%20comic%20panels%2C%20soft%20gradient%20background%20with%20subtle%20texture%2C%20dreamy%20atmospheric%20lighting%2C%20artistic%20webtoon%20aesthetic%2C%20warm%20color%20palette%20with%20rose%20and%20gold%20accents%2C%20clean%20minimalist%20composition&width=1600&height=800&seq=hero-bg-hanmanwu&orientation=landscape"
            alt="韩漫屋 - 韩国漫画大全免费在线观看"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/55" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 w-full max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4 leading-tight">
                韩国漫画大全
              </h1>
              <p className="text-base md:text-lg text-white/85 mb-8 max-w-xl mx-auto leading-relaxed">
                免费韩漫在线观看，下拉式阅读界面，收录最好看的韩国漫画。<br />
                恋爱、奇幻、动作、悬疑、搞笑——尽在<strong>韩漫屋</strong>。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/romance"
                  className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-semibold hover:bg-white/25 transition-all duration-300 whitespace-nowrap"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-8 mt-10 text-white/70 text-sm">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">5000+</p>
                  <p>漫画资源</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">8</p>
                  <p>精选分类</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">每日</p>
                  <p>持续更新</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-2">编辑精选的高分韩国漫画</p>
              </div>
              <Link
                to="/category/romance"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1"
              >
                查看全部 <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {featuredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - ${comic.description.slice(0, 30)}`}
                      title={`${comic.title} - 韩国漫画在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded">
                        {comic.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-white text-xs">
                        {comic.chapters}话 · {comic.status}
                      </p>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                漫画分类
              </h2>
              <p className="text-sm text-foreground-500 mt-2">八大精选分类，总有一款适合你</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300"
                >
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <i className={`${cat.icon} text-xl`} />
                  </span>
                  <div className="text-center">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-1">{cat.count}部作品</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  热门趋势
                </h2>
                <p className="text-sm text-foreground-500 mt-2">当下最受欢迎的韩国漫画</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {trendingComics.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group flex gap-3 p-3 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 hover:bg-background-100 transition-all duration-300"
                >
                  <div className="relative flex-shrink-0 w-16 h-20 overflow-hidden rounded-md">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - ${categoryLabels[comic.category] || comic.category}`}
                      title={`${comic.title} - 韩国漫画免费在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                    <span className="absolute top-0 left-0 w-5 h-5 flex items-center justify-center bg-accent-500 text-background-50 text-[10px] font-bold rounded-br-md">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[10px] px-1.5 py-0.5 bg-accent-100 text-accent-700 rounded">
                        {categoryLabels[comic.category] || comic.category}
                      </span>
                      <span className="text-[10px] text-foreground-400">
                        {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 阅读
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  最近更新
                </h2>
                <p className="text-sm text-foreground-500 mt-2">最新上架的韩国漫画章节</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {latestComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                    <img
                      src={comic.cover}
                      alt={`${comic.title} - 韩国漫画`}
                      title={`${comic.title} - 最新章节在线阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-medium rounded-full">
                        NEW
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                    {comic.title}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1">更新于 {comic.updatedAt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center mb-10">
              常见问题
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <h4 className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</h4>
                    <span className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-300 ${
                      activeFaq === index
                        ? 'bg-primary-500 text-background-50 rotate-45'
                        : 'bg-background-200 text-foreground-500'
                    }`}>
                      <i className="ri-add-line text-sm" />
                    </span>
                  </button>
                  {activeFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-4">
              准备好开始阅读了吗？
            </h2>
            <p className="text-sm text-foreground-500 mb-8">
              数千部韩国漫画等你发现，免费在线阅读，支持下拉式界面，随时随地畅享韩漫乐趣。
            </p>
            <Link
              to="/category/drama"
              className="inline-flex px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
            >
              立即探索韩漫
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}